<template>
  <div
    ref="sidebar"
    class="sidebar gc-fixed gc-bottom-0 gc-mx-4 sm:gc-mr-0 sm:gc-right-0"
    :class="
      open
        ? 'gc-rounded-t-3xl sm:gc-rounded-lt-3xl sm:gc-rounded-r-none is-open sm:gc-w-4/5'
        : 'gc-rounded-t-3xl sm:gc-rounded-l-3xl sm:gc-rounded-r-none sm:gc-bottom-32 gc-h-16 sm:gc-w-16 sm:gc-h-auto'
    "
  >
    <header
      class="gc-flex gc-justify-center gc-items-center gc-bg-primary gc-px-4"
      :class="
        open
          ? 'gc-rounded-t-3xl sm:gc-rounded-lt-3xl sm:gc-rounded-r-none sm:gc-place-content-between'
          : 'gc-rounded-t-3xl sm:gc-rounded-l-3xl sm:gc-rounded-r-none sm:gc-flex-col sm:gc-px-0 sm:gc-py-4'
      "
    >
      <nav
        class="gc-w-full gc-max-w-md gc-divide-y sm:gc-divide-y-0 gc-divide-white gc-divide-opacity-50"
        :class="
          open ? 'sm:gc-flex sm:gc-flex-row sm:gc-divide-x sm:gc-py-4' : ''
        "
      >
        <div
          class="toggle-sidebar gc-flex gc-justify-center gc-items-center gc-px-2 sm:gc-px-0"
        >
          <button
            class="toggle gc-visible sm:gc-invisible sm:gc-w-0"
            :class="open ? 'sm:gc-hidden' : ''"
            type="button"
            :aria-label="open ? $t('sidebar.collapse') : $t('sidebar.expand')"
            @click="toggleSidebar"
          >
            <font-awesome-icon
              :icon="open ? faArrowDown : faArrowUp"
              color="white"
            />
          </button>
          <p
            class="gc-text-white gc-text-nowrap gc-text-3xl gc-m-4 gc-p-0 gc-cursor-pointer md:gc-whitespace-no-wrap"
            :class="
              open
                ? 'sm:gc-my-0 sm:gc-mx-0 sm:gc-mr-4 lg:gc-w-64'
                : 'vertical-text'
            "
            @click="toggleSidebar"
          >
            {{ $t('sidebar.title') }}
          </p>
        </div>
        <ul class="gc-px-0 gc-py-4 sm:gc-py-0 gc-place-content-between">
          <li
            v-for="assistant in assistants"
            :key="assistant"
            class="gc-px-2 sm:gc-px-4 lg:gc-pl-8 lg:gc-pr-0"
            :class="[
              { active: isActive === assistant },
              { inactive: isActive !== assistant && isActive !== null },
            ]"
            :data-category="getCategoryId(assistant)"
          >
            <a
              class="gc-flex gc-flex-col lg:gc-flex-row gc-justify-center gc-items-center"
              href="#"
              @click.prevent="toggleContent(assistant)"
            >
              <i
                :class="['circle-icon', assistant]"
                class="gc-rounded-full gc-h-14 gc-w-14 gc-flex gc-items-center gc-justify-center"
              />
              <span
                class="gc-text-white gc-text-sm gc-text-center gc-justify-center gc-uppercase gc-leading-5 gc-pt-2 lg:gc-pt-0 lg:gc-pl-2"
                v-html="$t('assistants.' + assistant)"
              ></span>
            </a>
          </li>
        </ul>
      </nav>
      <button
        class="toggle gc-invisible sm:gc-visible gc-border-l gc-border-white"
        type="button"
        :aria-label="open ? $t('sidebar.collapse') : $t('sidebar.expand')"
        @click="toggleSidebar"
      >
        <font-awesome-icon
          :icon="open ? faArrowRight : faArrowLeft"
          color="#fff"
        />
      </button>
    </header>
    <div>
      <restore-alert
        v-if="!assistantStarted"
        v-show="open"
        :hasPersistedState="machine.hasPersistedState.value"
        @restore="
          restoreState();
          restoreToggleState();
          assistantStarted = true;
        "
        class="restore"
      />
      <section
        class="gc-overflow-y-auto"
        :class="['content', { show: open && isActive }]"
      >
        <div v-show="open && isActive" ref="content" :id="isActive + '-app'">
          <slot></slot>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import {
  PropType,
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';
import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
} from '@fortawesome/pro-regular-svg-icons';
import RestoreAlert from '@/components/RestoreAlert.vue';
import { StepDefinition } from '@/interfaces/step-definition';
import { AssistantMachine } from '@/interfaces/assistant';
import { AssistantType } from '~/consts/assistant-types';
import { StepCategoryId, StepId } from '~/consts/assistant-steps';
import { getCategoryId, getAssistantRootFrom } from '~/helper/converts';
import { loadStateFromLocalStorage } from '@/lib/storage/local-storage';
import { getQueryParamByName } from '@/lib/url/query-params';
import { sendEvent } from '@/lib/analytics/dataLayer';

const useParams = () => {
  const appDiv = document.getElementById(process.env.VUE_APP_SITE_NAME);
  const attribute = (name: string) => appDiv?.getAttribute(name) || null;

  // The entry point may come from the page URL, so a plain link - e.g. the one
  // behind a printed QR code - can start the assistant without the host page
  // having to render anything.
  //
  // A URL wins over the container attributes as a whole, never mixed with
  // them: the shop pages set data-assistant and data-stepid for their own
  // entry point (COMBI_INTEREST on the start page, HEATING_TYPE on the heat
  // pump page), and a link has to be able to open something else than that.
  const urlStepid = getQueryParamByName('assistant-step');
  const urlAssistant = getQueryParamByName('assistant');
  const entryFromUrl = Boolean(urlStepid || urlAssistant);

  const paramOpen =
    getQueryParamByName('assistant-open') || attribute('data-open');
  const paramStepid = entryFromUrl ? urlStepid : attribute('data-stepid');
  const paramAssistant = entryFromUrl
    ? urlAssistant
    : attribute('data-assistant');

  return { paramOpen, paramStepid, paramAssistant };
};

/**
 * Looks up an enum key from a parameter value. Values reaching us from a
 * printed URL cannot be corrected afterwards, so they are accepted in any
 * case and with dashes in place of underscores: HEAT_OR_COMBI, heat_or_combi
 * and heat-or-combi all resolve.
 */
const getEnumValue = <T extends Record<string, string>>(
  enumObject: T,
  value?: string | null,
): T[keyof T] | undefined => {
  if (!value) {
    return undefined;
  }

  const key = value.trim().toUpperCase().replace(/-/g, '_');
  return enumObject[key as keyof T];
};

export default defineComponent({
  components: {
    RestoreAlert,
  },
  props: {
    assistants: Array<AssistantType>,
    machine: {
      required: true,
      type: Object as PropType<AssistantMachine>,
    },
    steps: {
      required: true,
      type: Array as PropType<StepDefinition[]>,
    },
  },
  setup: (props) => {
    const { send, restoreState, currentStep } = props.machine;

    const assistantStarted = ref(false);

    const { paramOpen, paramStepid, paramAssistant } = useParams();
    const assistantActive = getEnumValue(
      AssistantType,
      paramAssistant,
    ) as AssistantType;

    const sidebar = ref<HTMLElement | null>(null);
    const open = ref(paramOpen === 'true' || false);
    const content = ref('');
    const isActive = ref<null | AssistantType>(assistantActive || null);

    const stepIds = Object.values(StepId);
    const stepId = ref(getEnumValue(StepId, paramStepid) as StepId);

    // Keep the active tile in sync with the machine. Steps reached by a jump
    // instead of a click on the sidebar - e.g. coming from an entry page that
    // routes into one of the assistants - have to activate the tile of their
    // category, because the submit step derives category, product and tracking
    // from it. Categories without a tile (root, submit) leave it untouched.
    watch(currentStep, (step) => {
      const assistantRoot = getAssistantRootFrom(step?.categoryId ?? '');
      if (assistantRoot && assistantRoot !== isActive.value) {
        isActive.value = assistantRoot;
      }
    });

    if (stepId.value && stepIds.includes(stepId.value)) {
      send(`JUMP_TO_${stepId.value.toUpperCase()}`);
      assistantStarted.value = true;
    } else if (assistantActive) {
      // No explicit step given: enter the assistant at its own first step. The
      // value of AssistantType is exactly that step's id.
      send(`JUMP_TO_${assistantActive.toUpperCase()}`);
      assistantStarted.value = true;
    }

    /**
     *  Google Tag Manager push dataLayer
     */
    const sendGAEvent = (assistant: AssistantType) => {
      if (assistant === AssistantType.SOLAR) {
        sendEvent({ eventName: 'shop_assistant_open_solar' });
      }
      if (assistant === AssistantType.HEATING) {
        sendEvent({ eventName: 'shop_assistant_open_waerme' });
      }
    };

    const toggleSidebar = () => {
      open.value = !open.value;
    };

    const toggleContent = (assistant: AssistantType) => {
      if (isActive.value !== assistant) {
        props.machine.restart();
        assistantStarted.value = true;
        send(`JUMP_TO_${assistant.toUpperCase()}`);
        isActive.value = assistant;
        sendGAEvent(assistant);
      }
    };

    const restoreToggleState = () => {
      const persistedState = loadStateFromLocalStorage();
      if (!currentStep.value) {
        return;
      }
      const categoryId = persistedState?.category
        ? persistedState?.category
        : currentStep.value.categoryId;
      currentStep.value.categoryId = categoryId as StepCategoryId;
      isActive.value = getAssistantRootFrom(categoryId as string);
      sendGAEvent(isActive.value as AssistantType);
    };

    const toggleButtons = document.querySelectorAll('.stawag-estore-toggle');

    const handleToggleClick = (event: Event) => {
      const toggleButton = event.currentTarget as HTMLElement;

      props.machine.restart();
      assistantStarted.value = true;

      const toggleOpen = () => {
        const categoryId: AssistantType =
          AssistantType[toggleButton.getAttribute('data-assistant') as string];
        isActive.value = getAssistantRootFrom(categoryId as string);
        stepId.value = toggleButton.getAttribute('data-stepid') as StepId;
        open.value = true;
        send(`JUMP_TO_${stepId.value.toUpperCase()}`);
      };
      setTimeout(toggleOpen, 300);
    };

    const handleClickOutside = () => {
      open.value = false;
    };

    const overlay = () => document.querySelector('.overlay');

    const addListeners = () => {
      if (toggleButtons.length > 0) {
        toggleButtons.forEach((toggleButton) => {
          toggleButton.addEventListener('click', handleToggleClick);
        });
      }
      overlay()?.addEventListener('click', handleClickOutside);
    };

    const removeListeners = () => {
      overlay()?.removeEventListener('click', handleClickOutside);
      toggleButtons.forEach((toggleButton) =>
        toggleButton.removeEventListener('click', handleToggleClick),
      );
    };

    // The backdrop belongs to the host component, so it can only be reached
    // once this one is mounted - which is also why this does not run as an
    // immediate watcher. An entry point coming from the URL opens the panel
    // before any of that is in the DOM, and without this the backdrop would
    // stay hidden, taking the click-outside handler on it along with it.
    const applyOpenState = () => {
      if (open.value) {
        overlay()?.classList.remove('hide');
        sendGAEvent(isActive.value as AssistantType);
      } else {
        overlay()?.classList.add('hide');
      }
    };

    watch(open, applyOpenState);

    onMounted(() => {
      addListeners();
      applyOpenState();
    });

    onBeforeUnmount(() => {
      removeListeners();
    });

    return {
      open,
      content,
      isActive,
      getCategoryId,
      toggleSidebar,
      toggleContent,
      faArrowUp,
      faArrowDown,
      faArrowLeft,
      faArrowRight,
      AssistantType,
      assistantStarted,
      restoreState,
      restoreToggleState,
    };
  },
});
</script>

<style lang="scss" scoped>
.sidebar {
  z-index: 900;
  background: #fff;
  transition: all 0.7s ease;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
  .vertical-text {
    transform: rotate(-180deg);
    writing-mode: vertical-lr;
  }

  @media only screen and (max-width: 767px) {
    max-height: 16rem;
    .horizontal-text,
    .vertical-text {
      transform: rotate(0deg);
      writing-mode: unset;
    }
    width: -webkit-fill-available;
    width: -moz-available;
    width: stretch;
  }
  & nav > ul {
    display: none;
    @media only screen and (max-width: 460px) {
      & > li span {
        display: none;
      }
    }
  }
  &.is-open {
    max-height: 85%;

    // The panel is fixed to the bottom edge and capped at 85% of the viewport,
    // so the steps have to scroll inside it. Laying it out as a flex column
    // makes the scroll container exactly as tall as the space the header
    // leaves over. A fixed height instead reaches past the bottom edge of the
    // viewport, leaves the container with nothing left to scroll, and the
    // touch gesture ends up scrolling the shop page behind it.
    display: flex;
    flex-direction: column;
    overflow: hidden;

    & > header {
      flex: 0 0 auto;
    }
    & > div {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;

      // Only the steps give way when the panel runs out of room, never the
      // restore hint above them.
      & > .restore {
        flex: 0 0 auto;
      }
    }
    & nav > ul {
      display: flex;
    }
    & section.content.show {
      flex: 1 1 auto;
      height: auto;
      min-height: 0;
      padding-bottom: 2rem;
      // Stop the gesture at the end of the steps instead of handing it on to
      // the page behind the panel.
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
    @media only screen and (min-width: 768px) {
      width: 90%;
      max-width: 1024px;
      button.toggle {
        border-left: 1px solid rgba(255, 255, 255, 0.5);
        padding-left: 1rem;
        height: 7rem;
      }
    }
    @media only screen and (min-width: 1024px) {
      button.toggle {
        height: 4rem;
      }
    }
    @media only screen and (min-width: 1024px) {
      & nav > ul > li {
        padding-left: 1rem;
      }
    }
  }
  & section.content {
    transition: all 0.3s ease-in-out;
    height: 0;
  }
  & .restore > div {
    margin: 0 !important;
  }
}
</style>
